<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { moveModalCommandsMap, normalizeString } from "@/modules/companyNeed/helpers/move";
import missionService from "@/modules/companyNeed/services/missionService";
import { MoveWorkerCompanyNeedCommands } from "@/modules/companyNeed/types/moveWorker";
import { MissionStatus } from "@/modules/companyNeed/views/details/missions/types";

import type { MoveWorker } from "@/modules/companyNeed/types/moveWorker";

import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";
import QuickActionsSeparator from "@/dsComponents/QuickActions/QuickActionsSeparator.vue";

const MOVE_MODAL_FIRST_GROUP_COMMANDS: MoveWorkerCompanyNeedCommands[] = [
    MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED,
    MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED,
    MoveWorkerCompanyNeedCommands.MOVE_TO_INTERVIEW,
    MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_PROPOSED,
    MoveWorkerCompanyNeedCommands.MOVE_TO_COMPANY_INTERVIEW,
    MoveWorkerCompanyNeedCommands.MOVE_TO_POOL,
];

const getSecondGroupCommands = (hasMissions: boolean): MoveWorkerCompanyNeedCommands[] => [
    ...(hasMissions ? [MoveWorkerCompanyNeedCommands.ADD_TO_ANOTHER_MISSION] : []),
    MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER,
];

const MOVE_MODAL_THIRD_GROUP_COMMANDS: MoveWorkerCompanyNeedCommands[] = [
    MoveWorkerCompanyNeedCommands.ADD_TO_OTHER_POOLS,
    MoveWorkerCompanyNeedCommands.MOVE_TO_ANOTHER_COMPANY_NEED,
];

const ALL_MODAL_COMMANDS = [...MOVE_MODAL_FIRST_GROUP_COMMANDS, ...getSecondGroupCommands(true), ...MOVE_MODAL_THIRD_GROUP_COMMANDS];

interface Props {
    workers: MoveWorker[];
    search: string;
    companyNeedId: string;
    hideMoveToAnotherNeed?: boolean;
    selectedIndex?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
    hideMoveToAnotherNeed: false,
    selectedIndex: null,
});

const { t } = useI18n();

const hasMissions = ref(false);

onMounted(async () => {
    try {
        const missions = await missionService.fetchMissions(props.companyNeedId, {
            status: [MissionStatus.TO_PROCESS, MissionStatus.TO_VALIDATE, MissionStatus.VALIDATED],
        });
        hasMissions.value = missions.length > 0;
    } catch {
        hasMissions.value = false;
    }
});

const resolveLabel = (item: { key: string; label: string }) => {
    if (item.key === MoveWorkerCompanyNeedCommands.CREATE_NEW_MISSION_WITH_THIS_WORKER) {
        return t("modals.companyNeed.moveWorker.createNewMissionFromProfiles", { count: props.workers.length }, props.workers.length);
    }
    return item.label;
};

const emit = defineEmits<{
    click: [command: MoveWorkerCompanyNeedCommands];
    "update:itemCount": [count: number];
}>();

const allowedCommands = computed(() => {
    const { workers } = props;
    if (workers.length === 0) return new Set<MoveWorkerCompanyNeedCommands>();

    const intersection = ALL_MODAL_COMMANDS.filter((command) =>
        workers.every((worker) => worker.availableCommands == null || worker.availableCommands.includes(command))
    );
    return new Set(intersection);
});

const filterBySearch = (commands: MoveWorkerCompanyNeedCommands[]) =>
    commands
        .map((command) => moveModalCommandsMap[command])
        .filter((item) => {
            if (!props.search) return true;
            return normalizeString(item.label).includes(normalizeString(props.search));
        });

const firstGroup = computed(() => {
    const restrictedActions = [
        MoveWorkerCompanyNeedCommands.MOVE_TO_PREQUALIFIED,
        MoveWorkerCompanyNeedCommands.MOVE_TO_CONTACTED,
        MoveWorkerCompanyNeedCommands.MOVE_TO_POOL,
    ];
    const baseActions = props.workers.length > 1 || props.hideMoveToAnotherNeed ? restrictedActions : MOVE_MODAL_FIRST_GROUP_COMMANDS;
    const actions = baseActions.filter((command) => allowedCommands.value.has(command));
    return filterBySearch(actions);
});

const secondGroup = computed(() => {
    const actions = getSecondGroupCommands(hasMissions.value).filter((command) => allowedCommands.value.has(command));
    return filterBySearch(actions);
});

const thirdGroup = computed(() => {
    const actions = MOVE_MODAL_THIRD_GROUP_COMMANDS.filter((command) => allowedCommands.value.has(command));
    return filterBySearch(actions);
});

const allVisibleCommands = computed(() => {
    const groups = [firstGroup.value, secondGroup.value];
    if (!props.hideMoveToAnotherNeed) groups.push(thirdGroup.value);
    return groups.flat();
});

watch(
    () => allVisibleCommands.value.length,
    (count) => emit("update:itemCount", count),
    { immediate: true }
);

defineExpose({
    executeSelected: (index: number) => {
        const command = allVisibleCommands.value[index];
        if (command) emit("click", command.key as MoveWorkerCompanyNeedCommands);
    },
});
</script>

<template>
    <template v-for="(item, index) in firstGroup" :key="item.key">
        <QuickActionItem :selected="selectedIndex === index" @click="emit('click', item.key)">
            <template #infos>
                <div class="move-quick-action-item__infos">
                    <i v-if="item.icon" :class="['ti', `ti-${item.icon}`, 'typo-md-regular']" :style="{ color: item.iconColor }" />
                    <span class="move-quick-action-item__infos-label typo-xs-medium">
                        {{ resolveLabel(item) }}
                    </span>
                </div>
            </template>
        </QuickActionItem>
    </template>

    <template v-for="(item, index) in secondGroup" :key="item.key">
        <QuickActionsSeparator v-if="index === 0 && firstGroup.length > 0" />
        <QuickActionItem :selected="selectedIndex === index + firstGroup.length" @click="emit('click', item.key)">
            <template #infos>
                <div class="move-quick-action-item__infos">
                    <i v-if="item.icon" :class="['ti', `ti-${item.icon}`, 'typo-md-regular']" :style="{ color: item.iconColor }" />
                    <span class="move-quick-action-item__infos-label typo-xs-medium">
                        {{ resolveLabel(item) }}
                    </span>
                </div>
            </template>
        </QuickActionItem>
    </template>

    <template v-if="!hideMoveToAnotherNeed">
        <template v-for="(item, index) in thirdGroup" :key="item.key">
            <QuickActionsSeparator v-if="index === 0" />
            <QuickActionItem :selected="selectedIndex === index + firstGroup.length + secondGroup.length" @click="emit('click', item.key)">
                <template #infos>
                    <div class="move-quick-action-item__infos">
                        <i v-if="item.icon" :class="['ti', `ti-${item.icon}`, 'typo-md-regular']" :style="{ color: item.iconColor }" />
                        <span class="move-quick-action-item__infos-label typo-xs-medium">
                            {{ resolveLabel(item) }}
                        </span>
                    </div>
                </template>
            </QuickActionItem>
        </template>
    </template>
</template>

<style scoped lang="scss">
.move-quick-action-item {
    width: 100%;

    &__infos {
        display: flex;
        align-items: center;
        gap: var(--spacing-lg, 12px);
        user-select: none;

        &-label {
            color: var(--text-sub-600, #494e61);
        }
    }
}

.search-result-item {
    &:hover {
        .move-quick-action-item__infos-label {
            color: var(--text-main-900, #00061a);
        }
    }

    &--selected {
        .move-quick-action-item__infos-label {
            color: var(--text-main-900, #00061a);
        }
    }
}
</style>
